import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../utils/api';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import LanguageSelector from '../components/common/LanguageSelector';

export const Login = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [heroOpen, setHeroOpen] = useState(false);
  const { login, loginGuest } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const urlEmail = searchParams.get('email');
    if (urlEmail) setEmail(urlEmail);
    const saved = localStorage.getItem('oferte_remember_email');
    if (saved && !urlEmail) { setEmail(saved); setRememberMe(true); }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (rememberMe) { localStorage.setItem('oferte_remember_email', email); }
    else { localStorage.removeItem('oferte_remember_email'); }

    try {
      const res = await fetch(`${API_URL}/api/auth/login-form`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.detail || t('auth.error_auth'));
        setLoading(false);
        return;
      }

      login(data.token, data.user);
      // Sesiune 36: TOTI userii (inclusiv admin) trec prin Welcome Page dupa login.
      navigate('/welcome');
    } catch (err) {
      setError(t('auth.error_network'));
    } finally {
      setLoading(false);
    }
  };

  const handleGuestLogin = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/guest`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || t('auth.error_auth'));
        setLoading(false);
        return;
      }
      loginGuest(data.token, data.user);
      navigate('/welcome');
    } catch (err) {
      setError(t('auth.error_network'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row items-stretch bg-slate-50">
      {/* Sesiune 39: hero image vizibil pe TOATE rezolutiile - micsorat pe mobil + click pentru marire. */}
      <div className="lg:w-1/2 relative flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 p-4 lg:p-8">
        <img
          src="/images/login-hero.jpeg"
          alt={t('auth.hero_alt')}
          onClick={() => setHeroOpen(true)}
          className="max-h-24 sm:max-h-40 lg:max-h-[90vh] max-w-full object-contain rounded-lg shadow-lg cursor-zoom-in"
          data-testid="login-hero-image"
        />
      </div>
      {heroOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setHeroOpen(false)}
          data-testid="login-hero-modal"
        >
          <img
            src="/images/login-hero.jpeg"
            alt={t('auth.hero_alt')}
            className="max-h-[95vh] max-w-[95vw] object-contain rounded-lg shadow-2xl"
          />
        </div>
      )}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
        <CardHeader className="text-center relative pt-12 sm:pt-6">
          <div className="absolute right-2 top-2 sm:right-4 sm:top-4 z-10">
            <LanguageSelector mode="login" />
          </div>
          <img src="/images/amel-logo.jpeg" alt="AMEL" className="mx-auto h-20 sm:h-24 mb-3 object-contain" style={{ mixBlendMode: 'multiply' }} data-testid="login-logo" />
        </CardHeader>
        <CardContent>
          <p className="text-center text-slate-900 font-semibold text-base mb-1" data-testid="login-access-title">{t('auth.access_title')}</p>
          <p className="text-center text-slate-600 text-sm mb-4" data-testid="login-subtitle">{t('app.subtitle')}</p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">{t('auth.email')}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('auth.email_placeholder')}
                required
              />
            </div>
            <div>
              <Label htmlFor="password">{t('auth.password')}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder=""
                required
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} className="w-4 h-4 rounded border-slate-300" />
              <span className="text-sm text-slate-600">{t('auth.remember_me')}</span>
            </label>
            {error && (
              <div className="text-red-500 text-sm text-center">{error}</div>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full py-3 rounded-md text-white font-bold text-sm shadow-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ backgroundColor: '#2E7D5B' }}
              onMouseEnter={(e) => { if (!loading) e.currentTarget.style.backgroundColor = '#25664A'; }}
              onMouseLeave={(e) => { if (!loading) e.currentTarget.style.backgroundColor = '#2E7D5B'; }}
            >
              {loading ? t('auth.login_loading') : t('auth.login_button')}
            </button>

            <div className="text-center mt-2">
              <a href="/uitat-parola" className="text-xs hover:underline" style={{ color: '#475569' }} data-testid="forgot-password-link">{t('auth.forgot_password')}</a>
            </div>

            <button
              type="button"
              onClick={handleGuestLogin}
              disabled={loading}
              data-testid="login-guest-btn"
              className="w-full py-3 rounded-md bg-white font-semibold text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ border: '2px solid #2E7D5B', color: '#2E7D5B' }}
              onMouseEnter={(e) => { if (!loading) e.currentTarget.style.backgroundColor = '#E8F5EE'; }}
              onMouseLeave={(e) => { if (!loading) e.currentTarget.style.backgroundColor = '#FFFFFF'; }}
            >
              {t('auth.guest_login')}
            </button>

            <div className="text-center text-sm text-slate-500 mt-6">
              <div>{t('auth.register_prompt')}</div>
              <a
                href="/register"
                data-testid="register-link"
                className="block mt-2 w-full py-3 rounded-md bg-white font-semibold text-sm transition-colors text-center"
                style={{ border: '2px solid #2E7D5B', color: '#2E7D5B' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#E8F5EE'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#FFFFFF'; }}
              >
                {t('auth.register_link')}
              </a>
            </div>
          </form>
        </CardContent>
      </Card>
      </div>
    </div>
  );
};

export default Login;
